// Dev-only mock-SDK simulator widget. Mounts in place of evisio2IframeWorkspaceApp
// when mock mode is engaged. Shows four tabs (Tools / Files / Events / State) backed
// by the MockEvisio2Connection state. The dev acts as the agent: lists/runs registered
// tools, injects/transitions thread files, emits any ConnectionEventMap event by hand,
// inspects the current view / mode / connection state.

import { compose, mdsWidget } from 'dojo/_base/declare';
import template from './evisio2SdkSimulator.html?raw';
import lang from 'dojo/_base/lang';
import _WidgetBaseSP from 'more/_WidgetBaseSP';

import { MockEvisio2Sdk } from "../mockSdk/mockEvisio2Sdk";
import { MockEvisio2Connection, MockStateChannel } from "../mockSdk/mockEvisio2Connection";
import SimulatorToolsTab from './tabs/simulatorToolsTab';
import SimulatorFilesTab from './tabs/simulatorFilesTab';
import SimulatorEventsTab from './tabs/simulatorEventsTab';
import SimulatorStateTab from './tabs/simulatorStateTab';

type TabKey = "tools" | "files" | "events" | "state";

class evisio2SdkSimulator extends compose(_WidgetBaseSP) {
    templateString: any = template;

    public sdk: MockEvisio2Sdk;

    private tabRow: HTMLDivElement;
    private toolsContainer:  HTMLDivElement;
    private filesContainer:  HTMLDivElement;
    private eventsContainer: HTMLDivElement;
    private stateContainer:  HTMLDivElement;

    private _toolsTab: MdsLax<InstanceType<typeof SimulatorToolsTab>>  | null = null;
    private _filesTab: MdsLax<InstanceType<typeof SimulatorFilesTab>>  | null = null;
    private _eventsTab: MdsLax<InstanceType<typeof SimulatorEventsTab>> | null = null;
    private _stateTab: MdsLax<InstanceType<typeof SimulatorStateTab>>  | null = null;

    private _tabButtons: { [K in TabKey]?: HTMLButtonElement } = {};
    private _activeTab: TabKey = "tools";

    private _stateChangeDisposer: (() => void) | null = null;

    constructor(args?: { sdk: MockEvisio2Sdk }) {
        super(...(arguments as unknown as []));
        if (args && args.sdk) this.sdk = args.sdk;
    }

    postCreate(): void {
        super.postCreate.apply(this, arguments as any);

        this._buildTabRow();
        this._instantiateTabs();
        this._switchTab("tools");

        let conn = this.sdk._getActiveConnection();
        if (conn){
            this._stateChangeDisposer = conn.addStateChangeListener(lang.hitch(this, this._onStateChange));
        }
    }

    startup(): void {
        super.startup.apply(this, arguments as any);
    }

    destroy(): void {
        if (this._stateChangeDisposer){ try { this._stateChangeDisposer(); } catch { /* ignore */ } this._stateChangeDisposer = null; }
        if (this._toolsTab)  { try { this._toolsTab.destroy(); } catch { /* ignore */ } }
        if (this._filesTab)  { try { this._filesTab.destroy(); } catch { /* ignore */ } }
        if (this._eventsTab) { try { this._eventsTab.destroy(); } catch { /* ignore */ } }
        if (this._stateTab)  { try { this._stateTab.destroy(); } catch { /* ignore */ } }
        super.destroy.apply(this, arguments as any);
    }

    private _buildTabRow(): void {
        let labels: Array<{ key: TabKey; label: string; icon: string }> = [
            { key: "tools",  label: "Tools",  icon: "fa-solid fa-screwdriver-wrench" },
            { key: "files",  label: "Files",  icon: "fa-solid fa-paperclip" },
            { key: "events", label: "Events", icon: "fa-solid fa-bell" },
            { key: "state",  label: "State",  icon: "fa-solid fa-circle-info" },
        ];
        for (let i = 0; i < labels.length; i++){
            let entry = labels[i];
            let btn = document.createElement("button");
            btn.type = "button";
            btn.title = entry.label;
            btn.innerHTML = "<i class=\"" + entry.icon + "\" style=\"margin-right:3px;\"></i>" + entry.label;
            btn.setAttribute("data-tab", entry.key);
            btn.style.cssText = "background:transparent; color:#cfd0d4; border:1px solid #3a3b41; border-radius:3px; padding:3px 6px; cursor:pointer; font-family:inherit; font-size:11px; flex:1 1 auto; min-width:0; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;";
            btn.addEventListener("click", lang.hitch(this, this._switchTab, entry.key));
            this.tabRow.appendChild(btn);
            this._tabButtons[entry.key] = btn;
        }
    }

    private _instantiateTabs(): void {
        let conn = this.sdk._getActiveConnection();
        if (!conn){
            // Shouldn't happen — workspace root awaits wiring.connect() before mounting the simulator —
            // but if it does, fall back to rendering nothing and log loudly.
            console.warn("[evisio2SdkSimulator] no active connection on the mock SDK at postCreate — tabs will be inert");
            return;
        }
        this._toolsTab  = new SimulatorToolsTab({ sdk: this.sdk, connection: conn });
        this._toolsTab.placeAt(this.toolsContainer);
        this._toolsTab.startup();

        this._filesTab  = new SimulatorFilesTab({ sdk: this.sdk, connection: conn });
        this._filesTab.placeAt(this.filesContainer);
        this._filesTab.startup();

        this._eventsTab = new SimulatorEventsTab({ sdk: this.sdk, connection: conn });
        this._eventsTab.placeAt(this.eventsContainer);
        this._eventsTab.startup();

        this._stateTab  = new SimulatorStateTab({ sdk: this.sdk, connection: conn });
        this._stateTab.placeAt(this.stateContainer);
        this._stateTab.startup();
    }

    private _switchTab(key: TabKey): void {
        this._activeTab = key;
        let containers: { [K in TabKey]: HTMLDivElement } = {
            tools:  this.toolsContainer,
            files:  this.filesContainer,
            events: this.eventsContainer,
            state:  this.stateContainer,
        };
        let keys: TabKey[] = ["tools", "files", "events", "state"];
        for (let i = 0; i < keys.length; i++){
            let k = keys[i];
            let active = (k === key);
            containers[k].style.display = active ? "" : "none";
            let btn = this._tabButtons[k];
            if (btn){
                btn.style.background = active ? "#3a3b41" : "transparent";
                btn.style.color = active ? "#ffffff" : "#cfd0d4";
                btn.style.borderColor = active ? "#5a5b61" : "#3a3b41";
            }
        }
    }

    private _onStateChange(_channel: MockStateChannel): void {
        // Connection status display lives on the State tab now; nothing to refresh in the header.
    }
}

export default mdsWidget(evisio2SdkSimulator);
